import Node from 'nanogl-node'

import { RunnerActor } from '@/state/RunnerStateMachine'
import { RenderContext } from '@/webgl/core/RenderContext'
import DebugDraw from '@/webgl/dev/debugDraw/DebugDraw'
import { IScene } from '@/webgl/engine/IScene'
import Renderer from '@/webgl/Renderer'
import GameWorld from '@/webgl/runner/GameWorld'
import { IRunnerRenderer } from '@/webgl/runner/interfaces/IRunnerRenderer'
import RunnerCamera from '@/webgl/runner/RunnerCamera'
import ScenarioAudio from '@/webgl/runner/scenarios/ScenarioAudio'
import { AudioSegment } from '@/webgl/runner/scenarios/segments/AudioSegment'
import { Outro } from '@/webgl/runner/scenarios/segments/Outro'
import { PlaySegment } from '@/webgl/runner/scenarios/segments/PlaySegment'
import { TransitionSegment } from '@/webgl/runner/scenarios/segments/TransitionSegment'
import RunnerRenderer from '@/webgl/runner/SceneRenderer'
import PostprocessSystem from '@/webgl/runner/systems/PostprocessSystem'
import SequencingSystem from '@/webgl/runner/systems/SequencingSystem'

import Countdown from '../segments/Countdown'
import DelaySegment from '../segments/DelaySegment'
import { Intro } from '../segments/Intro'
import MaskDoorSegment from '../segments/MaskDoorSegment'
import { Popin } from '../segments/Popin'
import Tooltip from '../segments/Tooltip'
import SETUP from './Caris1Setup'

export default class Caris1Scene implements IScene {
  glRenderer: Renderer
  root: Node
  camCtrl: RunnerCamera
  world: GameWorld
  renderer: IRunnerRenderer
  audio: ScenarioAudio

  constructor(renderer: Renderer) {
    this.glRenderer = renderer

    this.root = new Node()
    this.world = new GameWorld(this.root)
    this.camCtrl = new RunnerCamera(this.world)
    this.renderer = new RunnerRenderer(renderer, this.world, SETUP)

    this.world.getTrackingSystem().useDomElement(this.glRenderer.ilayer)

    this.renderer.useSmoke(false)

    // this.renderer.useRails(true);
    // this.renderer.useStatus(["confusion"]);

    this.world.registry.init(SETUP)
    this.world.systems.add(new PostprocessSystem(this.world, this.glRenderer.postprocess))
    this.world.background.spawnableItems = SETUP.sequences[0].scrolling

    this.audio = new ScenarioAudio('en', SETUP.audio, SETUP.audioSfx)

    const sequencing = new SequencingSystem(this.world)
    this.world.systems.add(sequencing)

    sequencing
      .init(this.renderer, SETUP)
      .add(new Intro('introduction'))
      .add(new AudioSegment('audio_intro'))
      .add(new Popin('popin1', true))
      .add(new Countdown('countdown'))

      .add(new Tooltip('move'))
      .add(new Tooltip('pause'))

      .add(new PlaySegment('supports'))
      .add(new DelaySegment('delay', 1.5))
      .add(new PlaySegment('policies'))
      .add(new DelaySegment('delay', 1.5))
      .add(new PlaySegment('education'))

      .add(new TransitionSegment('transition'))
      .add(new AudioSegment('audio_intro_syllable'))
      .add(new Popin('popin2'))

      .add(new Tooltip('stick'))

      .add(new PlaySegment('constitution_1'))
      .add(new PlaySegment('constitution_2'))
      .add(new PlaySegment('constitution_3'))
      .add(new PlaySegment('constitution_4'))
      .add(new DelaySegment('delay', 1.5))

      .add(new PlaySegment('diplomacy_1'))
      .add(new PlaySegment('diplomacy_2'))
      .add(new PlaySegment('diplomacy_3'))
      .add(new PlaySegment('diplomacy_4'))
      .add(new DelaySegment('delay', 1.5))

      .add(new PlaySegment('organization_1'))
      .add(new PlaySegment('organization_2'))
      .add(new PlaySegment('organization_3'))
      .add(new PlaySegment('organization_4'))
      .add(new PlaySegment('organization_5'))
      .add(new DelaySegment('delay', 1.5))

      .add(new TransitionSegment('transition_dictation'))

      // the specific role of diplomacy is
      // to avoid conflicts and faster cooperation
      .add(new Popin('popin3'))
      .add(new AudioSegment('audio_dictation_1'))

      .add(new PlaySegment('dictation_1'))
      .add(new DelaySegment('delay', 1.5))

      .add(new AudioSegment('audio_dictation_2'))
      .add(new PlaySegment('dictation_2'))

      .add(new MaskDoorSegment('mask_door'))

      .add(new PlaySegment('mask_world'))

      .add(new TransitionSegment('transition_mask_world'))

      .add(new AudioSegment('outro_bis'))

      .add(new Outro('outro'))

    /// #if _DEBUG
    const params = new URLSearchParams(window.location.search)
    if (params.get('segment')) {
      sequencing.setStartSegmentId(params.get('segment'))
    }
    /// #endif
  }

  public setActor(actor: RunnerActor) {
    this.world.state.setActor(actor)
  }

  async load(abort: AbortSignal): Promise<void> {
    await Promise.all([
      this.renderer.load(abort),
      this.world.systems.get<SequencingSystem>(SequencingSystem).load(abort),
      this.audio.load(abort),
    ])
  }

  start() {
    this.glRenderer.cameras.getManager('main').setControler(this.camCtrl)

    this.world.state.setJauge(0)
    this.world.start()
  }

  preRender(): void {
    this.world.update(this.glRenderer.dt)
    const time = this.world.time
    this.renderer.preRender(time)
    this.root.updateWorldMatrix()
  }

  rttPass(context: RenderContext): void {
    this.renderer.rttPass(context)
  }

  render(context: RenderContext): void {
    this.renderer.render(context)
    this.world.systems.forEach((system) => system.render?.(context))

    if (this.camCtrl.camera) DebugDraw.drawFrustum(this.camCtrl.camera._viewProj)
  }

  dispose(): void {
    this.world.dispose()
    this.audio.dispose()
  }
}
